// src/stores/runtimeStore.ts
// ============================================
// RuntimeStore - Runtime 状态总览
//
// 职责：
//   1. 维护所有 Runtime 的状态/健康度/延迟概览
//   2. 维护健康度汇总（healthy / degraded / unhealthy / unknown 计数）
//   3. fetchRuntimes 调用 RuntimeManager.getStatusSummary() + getLatencyStats()
//   4. 浏览器环境降级：RuntimeManager 不可用时返回空状态，不抛错
//
// 依赖：Zustand（项目已依赖）
// ============================================

import { create } from "zustand";
import RuntimeManager from "../runtime/RuntimeManager";
import type {
  RuntimeName,
  RuntimeHealth,
  RuntimeStatus as RuntimeStatusType,
} from "../types/runtime";

// ============================================
// 类型定义
// ============================================

/** 单个 Runtime 状态条目 */
export interface RuntimeTile {
  name: RuntimeName;
  status: RuntimeStatusType;
  health: RuntimeHealth;
  /** 调用次数（来自 LatencyStats） */
  calls: number;
  /** 平均延迟（毫秒） */
  avgLatency: number;
  /** 运行时长（秒） */
  uptime: number;
}

/** 健康度汇总 */
export interface HealthSummary {
  initialized: boolean;
  runtimeCount: number;
  readyCount: number;
  uptime: number;
  healthy: number;
  degraded: number;
  unhealthy: number;
  unknown: number;
}

export interface RuntimeState {
  /** 所有 Runtime 状态条目 */
  runtimes: RuntimeTile[];
  /** 健康度汇总 */
  healthSummary: HealthSummary;
  /** 加载状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 最后更新时间（ISO 字符串） */
  lastUpdated: string | null;
  /** 拉取 Runtime 状态总览 */
  fetchRuntimes: () => void;
}

// ============================================
// 工具函数
// ============================================

/** 判断 RuntimeManager 是否可用（浏览器环境降级） */
function isRuntimeAvailable(): boolean {
  try {
    return (
      !!RuntimeManager &&
      typeof RuntimeManager.getStatusSummary === "function"
    );
  } catch {
    return false;
  }
}

/** 空的健康度汇总 */
const EMPTY_SUMMARY: HealthSummary = {
  initialized: false,
  runtimeCount: 0,
  readyCount: 0,
  uptime: 0,
  healthy: 0,
  degraded: 0,
  unhealthy: 0,
  unknown: 0,
};

// ============================================
// Store 实现
// ============================================

export const useRuntimeStore = create<RuntimeState>((set) => ({
  runtimes: [],
  healthSummary: EMPTY_SUMMARY,
  loading: false,
  error: null,
  lastUpdated: null,

  fetchRuntimes: () => {
    // 浏览器环境降级
    if (!isRuntimeAvailable()) {
      set({
        runtimes: [],
        healthSummary: EMPTY_SUMMARY,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
      return;
    }
    set({ loading: true, error: null });
    try {
      const summary = RuntimeManager.getStatusSummary();
      const latency = RuntimeManager.getLatencyStats();

      // 构建 Runtime 状态条目
      const tiles: RuntimeTile[] = Object.keys(summary.status).map((name) => {
        const n = name as RuntimeName;
        const lat = latency.get(n);
        const health = summary.health[n] || "unknown";
        return {
          name: n,
          status: summary.status[n],
          health,
          calls: lat?.count ?? 0,
          avgLatency: lat?.avg ? Math.round(lat.avg) : 0,
          uptime: summary.uptime,
        };
      });

      // 计算健康度汇总
      const healthValues = Object.values(summary.health);
      const healthSummary: HealthSummary = {
        initialized: summary.initialized,
        runtimeCount: summary.runtimeCount,
        readyCount: summary.readyCount,
        uptime: summary.uptime,
        healthy: healthValues.filter((h) => h === "healthy").length,
        degraded: healthValues.filter((h) => h === "degraded").length,
        unhealthy: healthValues.filter((h) => h === "unhealthy").length,
        unknown: healthValues.filter((h) => h === "unknown").length,
      };

      set({
        runtimes: tiles,
        healthSummary,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        lastUpdated: new Date().toISOString(),
      });
    }
  },
}));

export default useRuntimeStore;
